import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { createRef, useRef } from 'react';
import { useScrollTransition } from 'util/react-transition-component';
import Text from '../../atoms/text/Text';
import styles from './CitiesNavigation.module.scss';
import { setupTransitionInTimeline } from './CitiesNavigation.transitions';
import type { CitiesNavigationProps, CitiesNavigationRef } from './CitiesNavigation.data';
import { useGlobalStore } from '../../../stores/global';
import PrimaryLink from '../../atoms/primary-link/PrimaryLink';
import { StorySlug } from '../../../util/storyblok/types/api.types';
import type { PrimaryLinkRef } from '../../atoms/primary-link/PrimaryLink.data';

function CitiesNavigation({
  blok,
  layoutState = 'heroHeader',
  disableTriggerInOnScroll = false,
  canTransitionIn,
  skipTransitionIn = false,
  onTransitionInComplete,
  ref,
  ...props
}: CitiesNavigationProps & { ref?: Ref<CitiesNavigationRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const createdByRef = useRef<HTMLParagraphElement>(null);
  const exploreRef = useRef<HTMLParagraphElement>(null);
  const allWorkRef = useRef<PrimaryLinkRef>(null);
  const locationsRef = useRef((blok.locations || []).map(() => createRef<PrimaryLinkRef>()));

  const store = useGlobalStore();

  useScrollTransition({
    scroll: {
      canTransitionIn,
      disableTriggerInOnScroll,
      trigger: elementRef,
      vars: {
        start: 'top bottom',
      },
    },
    setupOptions: () => ({
      refs: {
        createdByRef,
        locationsRef,
        exploreRef,
        allWorkRef,
        skipTransitionIn,
        onTransitionInComplete,
      },
      setupTransitionInTimeline,
    }),
  });

  return (
    <div ref={elementRef} className={classNames(styles.citiesNavigation, props.className)}>
      <div>
        <Text
          as="p"
          size="body-standard"
          disableTriggerInOnScroll={layoutState !== 'previewNext'}
          transitionInType={`${skipTransitionIn ? 'none' : 'splitLineRotateIn'}`}
          ref={createdByRef}
        >
          {store.globalSettings.label_case_created_by}:
        </Text>
        <ul>
          {blok.custom_locations?.length
            ? blok.custom_locations?.map((location, index) => (
                <li key={location._uid}>
                  <PrimaryLink
                    ref={locationsRef.current[index]}
                    textVariant="button-label-large"
                    link={{ href: location.link.cached_url }}
                    label={location.label}
                    disableTriggerInOnScroll={layoutState !== 'previewNext'}
                    transitionInType={`${skipTransitionIn ? 'none' : 'splitLineRotateIn'}`}
                    target={location.link.target}
                  />
                </li>
              ))
            : blok.locations?.map(
                (location, index) =>
                  location.full_slug && (
                    <li key={location.uuid}>
                      <PrimaryLink
                        ref={locationsRef.current[index]}
                        textVariant="button-label-large"
                        link={{ href: location.full_slug }}
                        label={
                          location?.content?.country || location?.content?.title || location?.name
                        }
                        // label below allows to retrieve city name if need be
                        // label={
                        //   location?.content?.office_details?.[0]?.offices?.[0]?.location || location.name
                        // }
                        disableTriggerInOnScroll={layoutState !== 'previewNext'}
                        transitionInType={`${skipTransitionIn ? 'none' : 'splitLineRotateIn'}`}
                      />
                    </li>
                  ),
              )}
        </ul>
      </div>
      <div>
        <Text
          as="p"
          size="body-standard"
          disableTriggerInOnScroll={layoutState !== 'previewNext'}
          transitionInType={`${skipTransitionIn ? 'none' : 'splitLineRotateIn'}`}
          ref={exploreRef}
        >
          {store.globalSettings.label_case_explore}:
        </Text>
        <PrimaryLink
          textVariant="button-label-large"
          link={{ href: StorySlug.Work }}
          label={store.globalSettings.label_case_all_work}
          disableTriggerInOnScroll={layoutState !== 'previewNext'}
          transitionInType={`${skipTransitionIn ? 'none' : 'splitLineRotateIn'}`}
          ref={allWorkRef}
        />
      </div>
    </div>
  );
}

export default CitiesNavigation;
